import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import {
  uploadImagesToFirebase,
  updateItem,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { useActionDialog } from "../ActionDialog";

function PropertyImage({ deal, onPhotoUploaded }) {
  const [{ deals }, dispatch] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [optimisticUrl, setOptimisticUrl] = useState(null);
  const [photoCleared, setPhotoCleared] = useState(false);

  const syncDealsContext = (dealDocId, newPhotos) => {
    if (!Array.isArray(deals) || deals.length === 0) return;
    const updated = deals.map((d) =>
      d.createdat === dealDocId ? { ...d, propertyphoto: newPhotos } : d,
    );
    dispatch({ type: actionType.STORE_DEALS, deals: updated });
  };

  const savedUrl = !photoCleared && deal?.propertyphoto?.length > 0
    ? deal.propertyphoto[0]
    : null;
  const displayUrl = optimisticUrl || savedUrl || "/assets/no-image.png";
  const hasPhoto = Boolean(optimisticUrl || savedUrl);

  const handleClick = () => {
    if (uploading) return;
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const dealDocId = deal?.createdat || deal?.id;
    if (!dealDocId) {
      toast.error("Can't upload — deal id missing.");
      console.error("PropertyImage: no createdat or id on deal", deal);
      return;
    }
    setUploading(true);
    try {
      const [url] = await uploadImagesToFirebase([file]);
      // Prepend the new URL so it shows first; keep any existing photos.
      const newPhotos = [url, ...(deal?.propertyphoto || [])];
      await updateItem("deals", dealDocId, { propertyphoto: newPhotos });
      syncDealsContext(dealDocId, newPhotos);
      setOptimisticUrl(url);
      toast.success("Photo updated.");
      onPhotoUploaded?.(url, newPhotos);
    } catch (err) {
      console.error("Property photo upload failed:", err);
      toast.error("Upload failed. Try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (deleting || uploading) return;
    const dealDocId = deal?.createdat || deal?.id;
    if (!dealDocId) {
      toast.error("Can't delete — deal id missing.");
      return;
    }
    const ok = await confirm({
      title: "Remove photo",
      body: "Remove the property photo?",
      note: "This only deletes the photo, the deal stays.",
      tone: "danger",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await updateItem("deals", dealDocId, { propertyphoto: [] });
      syncDealsContext(dealDocId, []);
      setOptimisticUrl(null);
      setPhotoCleared(true);
      toast.success("Photo removed.");
      onPhotoUploaded?.(null, []);
    } catch (err) {
      console.error("Property photo delete failed:", err);
      toast.error("Could not remove photo. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  const overlayLabel = uploading
    ? "Uploading…"
    : hasPhoto
    ? "Change photo"
    : "Click to upload a photo";

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="relative group">
        <button
          type="button"
          onClick={handleClick}
          disabled={uploading || deleting}
          className="relative block w-full cursor-pointer disabled:cursor-wait"
          aria-label={hasPhoto ? "Change property photo" : "Upload property photo"}
        >
          <img
            src={displayUrl}
            className="w-full h-72 object-cover"
            alt="Property"
          />
          {/* Hover overlay (or persistent prompt when no photo) */}
          <div
            className={`absolute inset-0 flex items-center justify-center transition-colors ${
              hasPhoto ? "bg-slate-900/0 group-hover:bg-slate-900/40" : "bg-slate-900/20 group-hover:bg-slate-900/40"
            }`}
          >
            <span
              className={`text-white font-semibold text-sm bg-slate-900/80 px-3 py-1.5 rounded transition-opacity ${
                hasPhoto ? "opacity-0 group-hover:opacity-100" : "opacity-90 group-hover:opacity-100"
              }`}
            >
              {overlayLabel}
            </span>
          </div>
        </button>
        {hasPhoto && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={uploading || deleting}
            className="absolute top-2 right-2 size-8 rounded-full bg-white/90 hover:bg-white text-red-600 flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
            aria-label="Remove photo"
            title="Remove photo"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
      <div className="p-5">
        <h2 className="text-xl font-semibold text-gray-800">
          {deal.propertyaddress}
        </h2>
        <p className="text-gray-500">{deal.propertyType}</p>
      </div>
      {dialog}
    </div>
  );
}

export default PropertyImage;
